import React, { useState, useMemo, useRef, useEffect, useCallback, Fragment } from 'react';
import { useTranslation } from 'react-i18next'
import {
    Progress,
    Button,
    Spinner,
    Input,
    FormFeedback,
    FormGroup,
    ListGroup,
    ListGroupItem,
    Media,
    CardText,
    Form,
    Label
} from 'reactstrap'
import bootbox from 'components/Modals/Bootbox'
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'components/Shared/Toast';
import clsx from 'clsx';
import DatePicker from 'components/Shared/DatePicker'
import produce from 'immer';

import { parseDate, formatCurrency, emailRegex } from 'utils'
import useOnClickOutside from 'hooks/useOnClickOutside'
import { ProfileCan } from 'context/profile'
import useKeyPress from 'hooks/useKeyPress'
import { fetchProperty, contractAction } from 'api/actions/contracts'
import { uploadFileV2 as uploadFileAction } from 'api/actions/file';
import { fetcher } from 'api/client'


async function uploadFile({ file }) {
    return uploadFileAction(file);
}

function Milestones({ contract = {}, refetchList, isTemplate }) {
    const { t } = useTranslation(['translation', 'validations', 'glossary'])
    const queryClient = useQueryClient();
    const [listMode, setListMode] = useState('calendar')
    const [progressTotal, setProgressTotal] = useState(0);
    const { data, status } = useQuery(['contract', contract._id, 'milestones'], ({ queryKey: [, contractId] }) => fetcher(fetchProperty(contractId, 'milestones', {}, isTemplate),), {
        enabled: !!contract._id,
        onSuccess: (data) => {
            if (data.length) {
                const total = data.length;
                const count = data.reduce((count, milestone) => {
                    if (milestone.completed) {
                        count += 1;
                    }

                    return count
                }, 0);
                const percentage = Math.floor((count / total) * 100);

                setProgressTotal(percentage)
            }
        },
        onError: (error) => {
            // CHECK CancelledError {silen: undefined}
            // toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
        },
    })

    const { mutate: mutateAddMilestone } = useMutation((payload) => fetcher(contractAction(contract._id, 'createmilestone', payload, isTemplate),), {
        onSuccess: (data, variables) => {
            toast.success(t('validations:messages.successfullyCreated', { context: 'male', entity: t('glossary:milestone') }))
            queryClient.invalidateQueries(['contract', variables.contractId, 'milestones'])
            queryClient.invalidateQueries(['contract', variables.contractId, 'timeline'])
        },
        onError: (error) => {
            toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
        },
    })

    const toggleListMode = () => {
        setListMode(currentMode => {
            if (currentMode === 'calendar') return 'list'
            return 'calendar'
        })
    }

    const handleAddMilestone = () => {
        mutateAddMilestone({
            contractId: contract._id,
            name: t('contracts.milestones.new'),
            expectedDate: new Date()
        })
    }

    return (
        <div className="h-100">
            <div className="detail p-2">
                <div className="d-flex justify-content-between mb-2">
                    <span>{t('contracts.milestones.progress')}: {progressTotal}%</span>
                    <span className="pointer calendar-view" onClick={toggleListMode}>
                        <i className={`fas fa-${listMode}`}></i>
                    </span>
                </div>
                <Progress
                    className="progress-md mb-2"
                    color="primary"
                    striped
                    value={progressTotal}
                />
                {/* <ProfileCan I="modify" this={contract}>
                    {
                        () =>  */}
                <div className="text-center border">
                    <Button
                        block
                        size="sm"
                        color="default"
                        data-action="add-milestone"
                        onClick={handleAddMilestone}
                    >
                        + {t('contracts.milestones.add')}
                    </Button>
                </div>
                {/* }
                </ProfileCan> */}
            </div>
            <div className="milestones-listing overflow-x-hidden" style={{height: 'calc(100% - 220px)'}}>
                {listMode === 'list' && <MilestoneSimpleList list={data} />}
                {listMode === 'calendar' && <MilestoneCalendarList contract={contract} list={data} refetchList={refetchList} isTemplate={isTemplate} />}

            </div>
        </div>

    )
};

export default Milestones;

function MilestoneCalendarList({ contract, list = [], refetchList, isTemplate }) {
    const { t } = useTranslation()
    const queryClient = useQueryClient();

    const { mutate: mutateUpdateMilestone } = useMutation((payload) => fetcher(contractAction(contract._id, 'updatemilestone', payload, isTemplate),), {
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries(['contract', variables.contractId, 'milestones'])
            queryClient.invalidateQueries(['contract', variables.contractId, 'timeline'])
        },
        onError: (error) => {
            toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
        },
    })

    const { mutate: mutateDeleteMilestone } = useMutation((payload) => fetcher(contractAction(contract._id, 'deletemilestone', payload, isTemplate),), {
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries(['contract', variables.contractId, 'milestones'])
            queryClient.invalidateQueries(['contract', variables.contractId, 'timeline'])
            toast.success(t('milestones.deleted_success'))
        },
        onError: (error) => {
            toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
        },
    })
    const { mutate: mutateAddAttachment } = useMutation((payload) => fetcher(contractAction(contract._id, 'addmilestoneattachment', payload, isTemplate),), {
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries(['contract', variables.contractId, 'milestones'])
            queryClient.invalidateQueries(['contract', variables.contractId, 'timeline'])
        },
        onError: (error) => {
            toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
        },
    })
    const { mutate: mutateUploadFile } = useMutation((file) => fetcher(uploadFile(file),), {
        onMutate: (variables) => {
            queryClient.cancelQueries(['contract', variables.contractId, 'milestones'])

            const previousValue = queryClient.getQueryData(['contract', variables.contractId, 'milestones'])

            queryClient.setQueryData(['contract', variables.contractId, 'milestones'], produce(draft => {

                const index = draft.findIndex(m => m._id === variables.milestoneId);
                if (index !== -1) {
                    if (!draft[index].attachments) draft[index].attachments = []
                    draft[index].attachments.push({
                        filename: variables.file.name,
                        isUploading: true,
                        name: variables.subject
                    })
                }

            }))

            return previousValue
        },
        onSuccess: async ({ url, fileId }, variables) => {
            await mutateAddAttachment({
                filename: variables.file.name,
                contractId: variables.contractId,
                milestoneId: variables.milestoneId,
                name: variables.subject,
                url,
                fileId,
            })
        },
        onError: (error, variables, previousValue) => {
            queryClient.setQueryData(['contract', variables.contractId, 'milestones'], previousValue)
            toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
        },
    })
    const { mutate: mutateDeleteMilestoneAttachment } = useMutation((payload) => fetcher(contractAction(contract._id, 'deletemilestoneattachment', payload, isTemplate),), {
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries(['contract', variables.contractId, 'milestones'])
            queryClient.invalidateQueries(['contract', variables.contractId, 'timeline'])
        },
        onError: (error) => {
            toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
        },
    })

    const handleConfirmCompleted = async (milestone) => {
        const resp = await bootbox.confirm(t('contracts.milestones.confirm_completed'));
        if (!resp) return;

        const payload = {
            ...milestone,
            contractId: contract._id,
            milestoneId: milestone._id
        }

        if (milestone.realDate) {
            payload.completed = true
            mutateUpdateMilestone(payload)

            return;
        }

        const confirm = await bootbox.confirm({
            closeButton: false,
            text: t('contracts.milestones.real_date_incomplete'),
            buttons: {
                cancel: {
                    label: t('contracts.milestones.set_new_date'),
                    color: 'secondary'
                },
                confirm: {
                    label: t('contracts.milestones.set_as_today'),
                },
            }
        })

        if (confirm) {
            payload.completed = true;
            payload.realDate = new Date();

            mutateUpdateMilestone(payload, {
                onSuccess: (data) => {
                    refetchList()
                }
            })

            return;
        } else {
            const result = await bootbox.prompt({
                title: t('contracts.milestones.realDate'),
                inputType: 'date'
            });
            if (result) {
                payload.completed = true;
                payload.realDate = new Date(result);
                mutateUpdateMilestone(payload, {
                    onSuccess: (data) => {
                        refetchList()
                    }
                })
            }
        }
    }


    const handleDateChange = (milestone, { name, date }) => {
        const payload = {
            ...milestone,
            contractId: contract._id,
            milestoneId: milestone._id,
            [name]: date
        }

        mutateUpdateMilestone(payload)
    }

    const handleNameChange = async (milestone, { newName, newResponsible, extra_recipient }) => {
        const payload = {
            ...milestone,
            contractId: contract._id,
            milestoneId: milestone._id,
            name: newName,
            extra_recipient,
            responsible: { ...milestone?.responsible, email: newResponsible }
        }

        mutateUpdateMilestone(payload, {
            onMutate: (variables) => {
                queryClient.cancelQueries(['contract', variables.contractId, 'milestones'])

                const previousValue = queryClient.getQueryData(['contract', variables.contractId, 'milestones'])

                queryClient.setQueryData(['contract', variables.contractId, 'milestones'], produce(draft => {

                    const index = draft.findIndex(m => m._id === milestone._id);
                    if (index !== -1) draft[index].name = variables.name
                }))

                return previousValue
            },
            onError: (error, variables, previousValue) => {
                queryClient.setQueryData(['contract', variables.contractId, 'milestones'], previousValue)
            }
        })
    }

    const handleDeleteMilestone = async (milestone) => {
        const res = await bootbox.confirm(t('contracts.milestones.confirm_delete', { name: milestone.name }));
        if (!res) return;

        mutateDeleteMilestone({
            contractId: contract._id,
            milestoneId: milestone._id,
        })
    }

    const handleDeleteMilestoneAttachment = async (milestone, attachment) => {
        const res = await bootbox.confirm(t('contracts.milestones.confirm_delete_attachment', { name: attachment.name }));
        if (!res) return;

        mutateDeleteMilestoneAttachment({
            contractId: contract._id,
            milestoneId: milestone._id,
            attachmentId: attachment._id,
        })
    }

    const handleAddAttachment = async (milestone, { file, subject }) => {
        await mutateUploadFile({
            milestoneId: milestone._id,
            contractId: contract._id,
            file,
            subject,
        })
    }


    return (
        <ListGroup>
            {
                list?.map(milestone => (
                    <MilestoneCalendarItem
                        key={milestone._id}
                        milestone={milestone}
                        contractId={contract._id}
                        onNameChange={handleNameChange}
                        onFileChange={handleAddAttachment}
                        onConfirmCompleted={handleConfirmCompleted}
                        onDateChange={handleDateChange}
                        onDelete={handleDeleteMilestone}
                        onDeleteAttachment={handleDeleteMilestoneAttachment}
                        isTemplate={isTemplate}
                    />
                ))
            }
            {
                !list?.length &&
                <ListGroupItem className="text-center">
                    {t('contracts.milestones.no_records')}
                </ListGroupItem>
            }
        </ListGroup>
    )
}

function Completed() {
    return (<span className="fa-stack text-success">
        <i className="far fa-circle fa-stack-2x"></i>
        {/* <i className="fas fa-minus fa-stack-1x"></i> */}
    </span>)
}

function Complete(rest) {
    return (<span className="fa-stack text-secondary pointer" {...rest}>
        <i className="far fa-circle fa-stack-2x"></i>
        <i className="fas fa-minus fa-stack-1x"></i>
    </span>)
}

function MilestoneCalendarItem({ milestone, contractId, onConfirmCompleted, onDateChange, onDelete, onNameChange, onFileChange, onDeleteAttachment, isTemplate }) {
    const { t, i18n } = useTranslation()
    const [isEditing, setIsEditing] = useState(false);
    const [errors, seterrors] = useState({ extraRecipientRefError: false, responsibleInputRefError: false })
    const nameInputRef = useRef(null);
    const extraRecipientRef = useRef(null);
    const responsibleInputRef = useRef(null);
    const fileInputRef = useRef(null);
    const newAttachmentNameRef = useRef(null);

    const { _id: milestoneId, completed, name, expectedDate, realDate, attachments, elementReferenced, responsible: { email } = {}, extra_recipient } = milestone;

    const handleNameChange = useCallback(async () => {
        const newName = nameInputRef?.current?.value || t('contracts.milestones.new');
        const newResponsible = responsibleInputRef?.current?.value || '';
        const extra = extraRecipientRef?.current?.value || '';
        const extraRecipientRefError = extra !== '' && !emailRegex.test(extra);
        const responsibleInputRefError = newResponsible !== '' && !emailRegex.test(newResponsible)
        seterrors({ extraRecipientRefError, responsibleInputRefError })
        if (extraRecipientRefError || responsibleInputRefError) return;
        await onNameChange(milestone, { newName, newResponsible, extra_recipient: extra })
        setIsEditing(false)
    }, [milestone, onNameChange, t])


    const enterPressed = useKeyPress('Enter');
    useOnClickOutside(nameInputRef, handleNameChange)
    useEffect(() => {
        if (enterPressed) {
            handleNameChange()
        }
    }, [enterPressed, handleNameChange])


    const handleConfirmCompleted = () => {
        if (!completed && !isTemplate) {
            onConfirmCompleted(milestone)
        }
    }

    const handleRealDateChange = (date) => {
        onDateChange(milestone, { name: 'realDate', date })
    }

    const handleExpectedDateChange = (date) => {
        onDateChange(milestone, { name: 'expectedDate', date })
    }


    const handleEditName = (name) => {
        if (!completed) {
            setIsEditing(name)
        }
    }

    const handleDelete = () => {
        onDelete(milestone)
    }

    const handleDeleteAttachment = (attachment) => () => {
        onDeleteAttachment(milestone, attachment)
    }

    const handleOpenFileChooser = async () => {
        const name = await bootbox.prompt(t('contracts.documents.attachment_name'));
        if (!name || name === '') return toast.error(t('contracts.documents.attachment_name_missing'));
        newAttachmentNameRef.current = name;
        fileInputRef.current.click()
    }

    const handleFileChange = (evt) => {
        const name = newAttachmentNameRef.current;

        onFileChange(milestone, {
            file: evt.target.files[0],
            subject: name,
        });

        newAttachmentNameRef.current = '';
        fileInputRef.current.value = null;
    }

    return (
        <ListGroupItem>
            <Media>
                <Media className="align-self-center mr-2">
                    {completed ?
                        <Completed />
                        :
                        <Complete onClick={handleConfirmCompleted} />
                    }
                </Media>
                <Media body>
                    <Form>
                        {!completed && (
                            <div className="d-flex float-right">
                                {!isTemplate &&
                                    <i
                                        className="fas fa-upload pointer"
                                        data-action="upload-attachment"
                                        title={t('contracts.documents.upload_attachment')}
                                        onClick={handleOpenFileChooser}
                                    />
                                }
                                <input
                                    type="file"
                                    hidden={true}
                                    id={milestoneId}
                                    accept=".pdf,.docx,.doc,image/*"
                                    onChange={handleFileChange}
                                    ref={fileInputRef}
                                />
                                <i
                                    className="fas fa-times text-danger pointer ml-2"
                                    title={t('contracts.milestones.delete')}
                                    data-action="delete-milestone"
                                    onClick={handleDelete}
                                />
                            </div>
                        )}
                        <FormGroup className="m-0" inline>
                            <Label><b>{t('milestones.name')}</b></Label>
                            {!isEditing && isEditing !== 'name' ?
                                <label data-action="editname" className={clsx('text-normal text-break w-100', !completed && 'pointer text-break border')} onClick={() => handleEditName('name')}>{name || t('milestones.name')}</label>
                                :
                                <Input bsSize="sm" type="text" id="name" name="name" innerRef={nameInputRef} defaultValue={name} autoFocus={isEditing === 'name'} placeholder={t('milestones.name')} />
                            }
                        </FormGroup>

                        <FormGroup className="m-0">
                            <Label><b>{t('milestones.responsible')}</b></Label>
                            {!isEditing && isEditing !== 'responsible' ?
                                <label className={clsx('text-normal w-100', !completed && 'pointer text-break border')} onClick={() => handleEditName('responsible')}>{email || t('milestones.responsible')}</label>
                                :
                                <>
                                    <Input invalid={errors.responsibleInputRefError} bsSize="sm" type="text" id="email" name="email" innerRef={responsibleInputRef} defaultValue={email} autoFocus={isEditing === 'responsible'} placeholder={t('contracts.milestones.add_email')} />
                                    <FormFeedback>{t('participants.emailNotValid')}</FormFeedback>
                                </>
                                // <input className={clsx("form-control w-100 input-sm", errors.responsibleInputRefError && 'text-danger')} type="text" id="email" name="email" ref={responsibleInputRef} defaultValue={email} autoFocus={isEditing === 'responsible'} placeholder={t('contracts.milestones.add_email')}/>
                            }
                        </FormGroup>


                        <label><b>{t('milestones.extra_recipient')}</b></label>

                        <FormGroup className="">
                            {!isEditing && isEditing !== 'extra_recipient' ?
                                <label className={clsx('text-normal w-100', !completed && 'pointer text-break border')} onClick={() => handleEditName('extra_recipient')}>{extra_recipient || t('milestones.extra_recipient')}</label>
                                :
                                <>
                                    <Input invalid={errors.extraRecipientRefError} bsSize="sm" type="text" id="extra_recipient" name="extra_recipient" innerRef={extraRecipientRef} defaultValue={extra_recipient} autoFocus={isEditing === 'extra_recipient'} placeholder={t('contracts.milestones.add_email')} />
                                    <FormFeedback>{t('participants.emailNotValid')}</FormFeedback>
                                </>
                                // <input className={clsx("form-control w-100 input-sm", errors.extraRecipientRefError && 'text-danger')} type="text" id="extra_recipient" name="extra_recipient" ref={extraRecipientRef} defaultValue={extra_recipient} autoFocus={isEditing === 'extra_recipient'} placeholder={t('contracts.milestones.add_email')}/>
                            }
                        </FormGroup>

                        <FormGroup className="">
                            <b>{t('contracts.milestones.expectedDate')}</b>:{' '}
                            <DatePicker
                                locale={i18n.language}
                                disabled={completed}
                                className={clsx('border text-capitalize w-100', completed && 'no-underline')}
                                selected={expectedDate ? new Date(expectedDate) : null}
                                onChange={handleExpectedDateChange}
                                // minDate={Date.now()}
                                customInput={<input type="text" style={{ fontSize: 11 }} />}
                                dateFormat="dd/MMM/yyyy"
                                placeholderText={t('contracts.milestones.pending')}
                            />
                            {/* <span href="#" className={clsx("as-link", completed && "no-underline")} data-action="change-expected-date">
                        {expectedDate ? parseDate(expectedDate, 'dd/MMM/yyyy')  : t('contracts.milestones.pending')}
                    </span>
                    <input type="text" className="milestone-change-expected-date" data-format="DD/MMM/yyyy" name="set-milestone-expected-date" style={{display: "none"}} placeholder={t('contracts.milestones.expectedDate')} /> */}
                        </FormGroup>
                        <FormGroup className="m-0">
                            <b>{t('contracts.milestones.realDate')}</b>:{' '}
                            <DatePicker
                                locale={i18n.language}
                                disabled={completed}
                                className={clsx('border text-capitalize w-100', completed && 'no-underline')}
                                selected={realDate ? new Date(realDate) : null}
                                onChange={handleRealDateChange}
                                // minDate={Date.now()}
                                customInput={<input type="text" style={{ fontSize: 11 }} />}
                                dateFormat="dd/MMM/yyyy"
                                placeholderText={t('contracts.milestones.pending')}
                            />
                            {/* <span className={clsx("as-link", completed && "no-underline")}  data-action="change-real-date">
                        {realDate ? parseDate(realDate, 'dd/MMM/yyyy')  : t('contracts.milestones.pending')}
                    </span>
                    <input type="text" className="milestone-change-real-date" data-format="DD/MMM/YYYY" name="set-milestone-real-date" style={{display: "none"}} placeholder={t('contracts.milestones.realDate')} /> */}
                        </FormGroup>

                        {elementReferenced &&
                            <span>
                                <a href={`/contracts/editor/${contractId}?select=${milestoneId}`}>
                                    {t('contracts.milestones.view_in_contract')}
                                </a>
                            </span>
                        }

                        {attachments &&
                            <Fragment>
                                <label className="milestone-attachment-title">
                                    {t('contracts.milestones.attachments')}
                                </label>
                                <ul className="milestone-attachment-detail">
                                    {attachments.map((attachment, i) => {
                                        return (
                                            <li key={`${attachment._id}-${i}`}>
                                                <a className="text-break" href={attachment.url} target="_blank" rel="noopener noreferrer">{attachment.name}</a>
                                                {!completed && !attachment.isUploading &&
                                                    <span
                                                        className="ml-2"
                                                        href="#"
                                                        data-action="delete-attachment"
                                                        title={t('contracts.milestones.delete_attachment')}
                                                        data-id={attachment._id}
                                                        data-name={attachment.name}
                                                        onClick={handleDeleteAttachment(attachment)}
                                                    >
                                                        <i className="fas fa-times text-danger" data-name="{{name}}" data-id="{{_id}}" />
                                                    </span>
                                                }
                                                {
                                                    attachment.isUploading &&
                                                    <span className="d-flex align-items-center">
                                                        {/* <Progress animated color="success" value={progress} /> */}
                                                        <Spinner className="align-self-center" style={{ width: '1rem', height: '1rem' }} /> <span className="ml-1"> Subiendo... </span>
                                                    </span>
                                                }

                                            </li>
                                        )
                                    })}
                                </ul>
                            </Fragment>
                        }
                    </Form>
                </Media>
            </Media>
        </ListGroupItem>
    )
}


function MilestoneSimpleList({ list = [] }) {
    const { t } = useTranslation()

    const events = useMemo(() => {
        return Array.from(list?.reduce((events, milestone) => {
            const startDate = new Date(milestone.expectedDate);
            const month = startDate.getMonth();
            const year = startDate.getFullYear();
            const key = `${month}-${year}`

            const savedMilestones = events.get(key) ?? []
            savedMilestones.push(milestone)

            events.set(key, savedMilestones)
            return events;
        }, new Map()))
    }, [list])

    return (
        <div>
            {events?.map(([key, milestones]) => {
                const [month, year] = key.split('-')

                return (
                    <ul key={`${month} - ${year}`}>
                        <b>{t(`records.schedule.months.${month}`)} {year}</b>
                        {
                            milestones.map((milestone, i) => (
                                <li key={`${milestone._id}-${i}`}>
                                    {parseDate(milestone.expectedDate, 'dd/MMM/yyyy')} - {milestone.name}
                                </li>
                            ))
                        }
                    </ul>
                )
            })}
            {
                !events?.length &&
                <div className="text-center px-3 w-100">
                    {t('contracts.milestones.no_records')}
                </div>
            }
        </div>
    )
}

